import type { AfterViewInit, ElementRef, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import type { ControlValueAccessor } from '@angular/forms';
import { FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import type { ITimezone } from '@cf/core';
import { TIMEZONE_FLAT_LIST, TIMEZONE_LIST } from '@cf/core';
import {
  FormFieldComponent,
  InputDirective,
  InputPrefixDirective,
} from '@cf/shared/forms/form-field';
import { CfIconDirective } from '@cf/shared/ui/icons';
import { HighlightSearchPipe, TextComponent } from '@cf/shared/ui/typography';
import { AccountService, TimeFormatEnum } from '@cf/temp/services';
import { NgbDropdown, NgbDropdownMenu, NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { DateTime } from 'luxon';
import { debounceTime } from 'rxjs/operators';
import { TimezoneNamePipe } from './pipes/timezone-name.pipe';

@Component({
  selector: 'cf-timezone-select',
  templateUrl: './timezone-select.component.html',
  styleUrls: ['./timezone-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => TimezoneSelectComponent),
    },
  ],
  imports: [
    NgbDropdown,
    NgbDropdownToggle,
    TextComponent,
    HighlightSearchPipe,
    MatTooltip,
    TimezoneNamePipe,
    NgbDropdownMenu,
    ReactiveFormsModule,
    FormFieldComponent,
    InputDirective,
    InputPrefixDirective,
    CfIconDirective,
    NgxTolgeeModule,
  ],
})
export class TimezoneSelectComponent implements ControlValueAccessor, OnInit, AfterViewInit {
  private readonly accountService = inject(AccountService);
  private readonly cd = inject(ChangeDetectorRef);

  @ViewChild('listElementWrapper')
  listElementWrapper!: ElementRef<HTMLDivElement>;

  @ViewChild(NgbDropdown) dropdown!: NgbDropdown;

  @ViewChild(InputDirective) cfInput!: InputDirective;

  @Input() timeFormat!: number;

  @Input() date!: string;

  @Input() disabled!: boolean;

  timezoneList = TIMEZONE_LIST;

  timezone!: string;

  searchControl = new FormControl();

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange: any = () => {};

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched = () => {};

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: never): void {
    this.onTouched = fn;
  }

  get luxonTimeFormat() {
    if (this.timeFormat) {
      if (this.timeFormat === 12) {
        return { ...DateTime.TIME_SIMPLE, hour12: true };
      } else if (this.timeFormat === 24) {
        return DateTime.TIME_24_SIMPLE;
      }
    }
    if (this.accountService.account.time_format === TimeFormatEnum['12h']) {
      return { ...DateTime.TIME_SIMPLE, hour12: true };
    } else {
      return DateTime.TIME_24_SIMPLE;
    }
  }

  ngOnInit() {
    this.searchControl.valueChanges.pipe(debounceTime(200)).subscribe((res) => {
      const regex = new RegExp(res, 'i');

      this.timezoneList = TIMEZONE_LIST.filter((itemGroup) => {
        itemGroup.result = TIMEZONE_FLAT_LIST.filter((item) => {
          if (item.category === itemGroup.groupName) {
            return Object.values(item).find((value) => regex.test(value));
          }
          return false;
        });
        return itemGroup.result.length;
      });

      this.cd.detectChanges();
    });
  }

  ngAfterViewInit() {
    this.dropdown.openChange.subscribe((opened) => {
      if (opened) {
        setTimeout(() => {
          const element = document.getElementById(this.timezone);
          const offset = element?.offsetTop;
          if (offset) {
            this.listElementWrapper.nativeElement.scrollTop = offset - 100;
          }
          this.cfInput?.elementRef?.nativeElement?.focus();
        }, 0);
      }
    });
  }

  writeValue(timezone: string) {
    this.timezone = timezone;
    this.cd.markForCheck();
  }

  onModelChange(timezone: ITimezone) {
    this.dropdown.close();
    this.timezone = timezone.value;

    this.onChange(timezone.value);
  }

  getTime(item: any) {
    return DateTime.local({ zone: item.value }).toLocaleString(this.luxonTimeFormat);
  }
}
